import { create } from 'zustand';
import { ICleaner, IDrawer, IObject, IOta } from './types';
import { API } from './api';

interface IState {
  loaded: boolean;
  agency: any;
  rentId: any;
  objects: IObject[];
  cleaners: ICleaner[];
  drawer: IDrawer;
  sources: any;
  messages: any[];
  instructions: any;
  otas: IOta[];
  cur: any;
  role: any;
  userId: string | null;
  setDrawer: (drawer: Partial<IDrawer>, cur?: any) => void;
  setCur: (cur: any) => void;
  closeDrawer: () => void;
  addHistory: (name: string, data?: any) => Promise<void>;
  removeHistory: (id: number) => Promise<void>;
  addPayment: (amount?: number, invoice_id?: number) => Promise<void>;
  deletePayment: () => Promise<void>;
  changeRent: (dto: any) => Promise<void>;
  changeObject: (dto: any) => Promise<void>;
  getRent: (id: any) => void;
  getClean: (id: any) => Promise<void>;
  createTopic: () => Promise<void>;
  editCurClean: (dto: any) => void;
  setObjects: (objects: IObject[]) => void;
  getInitialData: () => void;
}

const EMPTY_DRAWER: IDrawer = {
  open: false,
  // cur: null,
  name: 'empty',
};

export const useStore = create<IState>((set, get) => ({
  loaded: false,
  agency: null,
  rentId: null,
  objects: [],
  cleaners: [],
  messages: [],
  otas: [],
  drawer: EMPTY_DRAWER,
  cur: null,
  sources: null,
  role: null,
  instructions: null,
  userId: null,
  setCur: (cur) => set({ cur }),
  setDrawer: (newDrawer, cur) => {
    set((state) => {
      let changes: any = {
        drawer: {
          ...state.drawer,
          ...newDrawer,
        },
      };
      if (cur) {
        changes = {
          ...changes,
          cur,
        };
      }
      return changes;
    });
  },
  closeDrawer: () => set({ drawer: EMPTY_DRAWER, cur: null }),
  changeRent: async (dto) => {
    await API.rents.changeRent(dto);
    set((state) => {
      const cur = state.cur;

      const depositInfo =
        dto.defaultDeposit !== undefined
          ? {
              ...cur.depositInfo,
              all: Number(dto.defaultDeposit),
              paid: cur.depositInfo?.paid ?? 0,
              needToPay: dto.defaultDeposit - (cur.depositInfo?.paid ?? 0),
            }
          : cur.depositInfo;

      return { cur: { ...cur, ...dto, depositInfo } };
    });
  },
  changeObject: async (dto) => {
    await API.objects.changeObject(dto);
    set((state) => ({
      cur: { ...state.cur, object: { ...state.cur.object, ...dto } },
    }));
  },
  getRent: async (id) => {
    set({
      drawer: {
        open: true,
        name: 'rent',
        // cur: rent
      },
      rentId: id,
    });
  },
  getClean: async (id) => {
    const clean = await API.cleans.getClean(id);
    if (clean) {
      set({ drawer: { open: true, name: 'clean', mode: 'view' }, cur: clean });
    }
  },
  newClean: async () => {
    set({ drawer: { open: true, name: 'clean' }, cur: null });
  },
  setObjects: async (objects) => {
    set({ objects });
  },
  addHistory: async (name: any, data: any) => {
    const { cur, userId } = get();
    const rent_id = cur.id;
    const dto: any = { name, rent_id, data };
    if (data?.role === 'manager' && userId) {
      dto.user_id = userId;
    }
    const { id } = await API.history.addHistory(dto);
    set((state) => ({
      cur: {
        ...state.cur,
        histories: [
          ...state.cur.histories,
          {
            id,
            rent_id: state.cur.rent_id,
            name,
            data,
          },
        ],
      },
    }));
  },
  removeHistory: async (id: number) => {
    await API.history.removeHistory(id);
    set((state) => ({
      cur: { ...state.cur, histories: state.cur.histories.filter((item: any) => item.id !== id) },
    }));
  },
  deletePayment: async () => {
    const { cur } = get();
    const rent_id = cur.id;
    const name = 'payment';
    await API.history.deleteHistory({ name, rent_id });
    set((state) => ({
      cur: {
        ...state.cur,
        histories: state.cur.histories.filter((item: any) => item.name !== name),
        checkManualPayment: { needToCheck: false },
      },
    }));
  },
  createTopic: async () => {
    const { cur } = get();
    const rent_id = cur.id;
    const result = await API.rents.createTopic(rent_id);
    if (result.topic_id) {
      set((state) => ({
        cur: { ...state.cur, topic_id: result.topic_id },
      }));
    }
  },
  addPayment: async (amount, invoice_id) => {
    const { cur } = get();
    const rent_id = cur.id;
    amount = amount || cur.paymentInfo.needToPay;
    let data: any = { object_id: cur.object_id, amount, rent_id };
    if (invoice_id) {
      data = { ...data, invoice_id };
    }
    const response = await API.rents.addPayment(data);
    if (response) {
      set((state) => ({
        cur: {
          ...state.cur,
          checkManualPayment: false,
          paymentInfo: { ...state.cur.paymentInfo, needToPay: state.cur.paymentInfo.all - amount!, paid: amount },
        },
      }));
    } else {
      console.log('ERROR');
    }
  },
  editCurClean: (dto) => {
    set((state) => ({ cur: { ...state.cur, ...dto } }));
  },
  getInitialData: async () => {
    const user = await API.users.checkUser();
    if (user.active) {
      const [cleaners, objects, agency, sources, instructions, messages, otas] = await Promise.all([
        API.cleaners.getCleaners(),
        API.objects.getObjects(),
        API.agency.getSettings(),
        API.constants.getSources(),
        API.objects.getInstructionsExamples(),
        API.messages.getMessages(),
        API.otas.getOtas(),
      ]);

      set({
        cleaners,
        objects,
        agency,
        sources,
        otas,
        instructions,
        messages,
        role: user.role || 'admin',
        userId: user.id,
        loaded: true,
      });
    }
  },
}));
